<template>
  <transition name="fade" :duration="{ enter: 600, leave: 300 }">
    <VueDraggableResizable
      v-if="isVisible || forcePopup"
      :w="width"
      :h="height"
      :x="position.x"
      :y="position.y"
      :draggable="isDragging"
      :resizable="true"
      style="position: absolute; z-index: 1000"
      @dragstop="onDragStop"
      @resizestop="onResizeStop"
    >
      <div class="dialog-container">
        <div class="dialog">
          <ChatHeader
            :title="strategy.getStrategyTitle()"
            :description="chatDescription"
            @close="closeChatBox"
            @clear="clearHistory"
            @mousedown.native="startDrag"
            @mouseup.native="stopDrag"
          />

          <ChatBody
            :messages="messagesFromStore"
            :style="{ opacity: showLoading ? '0.3' : '1' }"
          />

          <SpinningWheel v-if="showLoading" />
          <LoadingBar
            v-if="showLoading"
            :progressValue="loadingProgress"
            :message="loadingMessage"
          />

          <ChatFooter
            @send="addMessage"
            @regenerate="regenerateSummary"
            :showRegenerate="true"
            :disableRegenerate="this.showLoading"
          />
        </div>
      </div>
    </VueDraggableResizable>
  </transition>
</template>

<script>
// eslint-disable-next-line import/no-extraneous-dependencies
import VueDraggableResizable from 'vue-draggable-resizable';
import { v4 as uuidv4 } from 'uuid';
import ChatHeader from './components/ChatHeader';
import ChatBody from './components/ChatBody';
import ChatFooter from './components/ChatFooter';
import SpinningWheel from './components/SpinningWheel';
import LoadingBar from './components/LoadingBar';

import amplifyEventService from '../../services/aws-amplify/amplifyEvent';
import { SEND_REPAIR_SELECT_REQUEST } from '../../store/repairselect/actions/actionTypes';
import { REPAIR_SELECT_REQUEST } from '../../store/jobs/actions/actionTypes';

export default {
  components: {
    ChatHeader,
    ChatBody,
    ChatFooter,
    VueDraggableResizable,
    SpinningWheel,
    LoadingBar,
  },
  props: {
    isVisible: Boolean,
    title: String,
    message: String,
    chatboxMetadata: {
      type: Object,
      default: () => ({}),
      required: false,
    },
    strategy: {
      type: Object,
      required: false,
    },
  },
  data() {
    return {
      isDragging: false,
      position: { x: window.innerWidth - 100, y: window.innerHeight - 720 },
      width: 480,
      height: 600,
      showLoading: false,
      loadingProgress: 0,
      loadingMessage: 'Returning answer..',
      channel: null,
      chatId: uuidv4(),
      chatPreviousMessage: null,
      forcePopup: false,
      summaryAlreadyRequested: false,
    };
  },

  watch: {
    $props: {
      deep: true,
      immediate: true,
      handler({ strategy }) {
        if (!strategy || typeof strategy.getIdentity !== 'function') return;

        if (this.isVisible) {
          this.handleVisibilityChange();
        }
      },
    },

    message(newMessage) {
      if (!this.sessionKey) return;

      const initialMessage = { text: newMessage, isUser: false };

      const messagesGetter = this.$store.getters['ChatModule/messages'];

      const existingMessages =
        typeof messagesGetter === 'function'
          ? messagesGetter(this.sessionKey)
          : [];

      if (!existingMessages.length) {
        this.$store.dispatch('ChatModule/appendMessage', {
          sessionKey: this.sessionKey,
          message: initialMessage,
        });
      }
    },

    sessionKey: {
      immediate: true,
      handler(newKey) {
        if (!newKey) return;

        this.$store.dispatch('ChatModule/initSession', newKey);
      },
    },
  },

  async mounted() {
    this.$store.dispatch('ChatModule/cleanOldSessions', 30);
    console.log('[ChatBox] sessionKey =', this.sessionKey);

    this.channel = await amplifyEventService.connectToChannel('/dij/*');
    this.setupChannelSubscription();
  },

  computed: {
    chatDescription() {
      return this.strategy?.getStrategyDescription
        ? this.strategy.getStrategyDescription(this.chatboxMetadata)
        : '';
    },

    sessionKey() {
      if (!this.strategy) return null;

      const strategyType = this.strategy.type || 'unknown';

      const entityId = this.chatboxMetadata?._id || 'unknown';

      const rawIdentity =
        (this.strategy.getIdentity &&
          this.strategy.getIdentity(this.chatboxMetadata)) ||
        'default';

      const identityId = String(rawIdentity);

      return `${strategyType}:${entityId}-${identityId}`;
    },

    messagesFromStore() {
      if (!this.sessionKey) return [];

      const getter = this.$store.getters['ChatModule/messages'];
      return typeof getter === 'function' ? getter(this.sessionKey) : [];
    },

    interactionsFromStore() {
      if (!this.sessionKey) return [];

      const getter = this.$store.getters['ChatModule/interactions'];
      return typeof getter === 'function' ? getter(this.sessionKey) : [];
    },
  },

  methods: {
    resetChatBox() {
      if (!this.sessionKey) return;

      const initialMessage = { text: this.message, isUser: false };

      this.$store.dispatch('ChatModule/appendMessage', {
        sessionKey: this.sessionKey,
        message: initialMessage,
      });

      this.chatPreviousMessage = null;
      this.summaryAlreadyRequested = false;
      this.chatId = uuidv4();
      this.initializeDialogPosition();

      const shouldAutoSubmit = this.strategy?.shouldAutoSubmit?.() ?? true;
      if (shouldAutoSubmit) {
        this.sendRequest();
      }
    },

    regenerateSummary() {
      this.sendRequest(true);
    },
    setupChannelSubscription() {
      this.channel.subscribe({
        next: (data) => {
          let { event } = data;
          if (typeof event === 'string') {
            try {
              event = JSON.parse(event);
            } catch (e) {
              console.error('Failed to parse event', e);
            }
          }

          if (event.provided_id === this.chatId) {
            this.loadingProgress = event.progress_level;
            if (event.message) {
              this.loadingMessage = event.message;
            }
            // eslint-disable-next-line no-console
            console.log(
              '[ChatBox] progress update:',
              this.loadingProgress,
              'message:',
              event.message
            );

            if (event.result || event.error) {
              setTimeout(() => {
                const result = this.strategy.onMessageReceived(event);

                if (result.isError) {
                  this.addSystemMessage(result.text);
                  this.toggleLoading(false);
                } else {
                  if (!this.isVisible) {
                    this.$buefy.toast.open({
                      duration: 5000,
                      message: 'Summary generated!',
                      type: 'is-success',
                      position: 'is-bottom',
                    });
                    this.forcePopup = true;
                  }

                  const replyMessage = {
                    text: result.text || '',
                    documents: result.documents || [],
                    isUser: false,
                    canBeCopied: true,
                  };

                  if (this.sessionKey) {
                    this.$store.dispatch('ChatModule/appendMessage', {
                      sessionKey: this.sessionKey,
                      message: replyMessage,
                    });

                    if (result.documents && result.documents.length) {
                      this.$store.dispatch('ChatModule/appendDocuments', {
                        sessionKey: this.sessionKey,
                        documents: result.documents,
                      });
                    }
                  }

                  this.chatPreviousMessage = result.text;
                  this.toggleLoading(false);
                }
                this.loadingProgress = 0;
              }, 1500);
            }
          }
        },
        error: (err) => console.error('Subscription error:', err),
      });
    },
    toggleLoading(state) {
      this.showLoading = state;
    },
    getCurrentTimestamp() {
      const now = new Date();
      return `${now.getHours().toString().padStart(2, '0')}h:${now
        .getMinutes()
        .toString()
        .padStart(2, '0')}m:${now.getSeconds().toString().padStart(2, '0')}s`;
    },
    createInteraction(content = 'no user interaction content') {
      const sanitizeString = (value) =>
        typeof value === 'string' ? value.replace(/'/g, '') : value;

      const currentInteractions = this.sessionKey
        ? this.interactionsFromStore
        : [];

      return {
        interactionId: currentInteractions.length + 1,
        timestamp: this.getCurrentTimestamp(),
        reply: {
          type: 'reply type',
          content: sanitizeString(this.chatPreviousMessage) || '',
        },
        userInteraction: {
          type: 'user interaction type',
          content,
        },
      };
    },

    createPayload(forceRegenerate) {
      if (forceRegenerate && this.sessionKey) {
        this.$store.dispatch('ChatModule/replaceInteractions', {
          sessionKey: this.sessionKey,
          interactions: [],
        });
      }

      const interactions =
        this.sessionKey && this.interactionsFromStore.length
          ? this.interactionsFromStore
          : [];

      return this.strategy.createPayload(
        this.chatId,
        this.chatboxMetadata,
        interactions
      );
    },

    async handleVisibilityChange() {
      this.initializeDialogPosition();

      const currentStrategyType = this.strategy?.type ?? null;
      const currentEntityIdentifier =
        this.strategy?.getIdentity(this.chatboxMetadata) ?? null;

      const storedSettings = this.getStoredStrategySettings();
      const storedStrategyType = storedSettings?.strategyType ?? null;
      const storedEntityIdentifier = storedSettings?.entityIdentifier ?? null;

      const strategyChanged = currentStrategyType !== storedStrategyType;
      const identityChanged =
        currentEntityIdentifier !== storedEntityIdentifier;

      if (strategyChanged || identityChanged) {
        this.resetChatBox();
        this.saveStrategySettings(currentStrategyType, currentEntityIdentifier);
        return;
      }

      const shouldAutoSubmit = this.strategy?.shouldAutoSubmit?.() ?? true;
      if (!this.summaryAlreadyRequested && shouldAutoSubmit) {
        await this.sendRequest();
      }
    },

    getStoredStrategySettings() {
      try {
        const raw = localStorage.getItem('chatBoxStrategy');
        return raw ? JSON.parse(raw) : null;
      } catch (e) {
        console.warn('Failed to parse stored strategy settings', e);
        return null;
      }
    },

    saveStrategySettings(type, identifier) {
      const settings = {
        strategyType: type,
        entityIdentifier: identifier,
      };
      localStorage.setItem('chatBoxStrategy', JSON.stringify(settings));
    },

    async sendRequest(forceRegenerate) {
      try {
        const payload = this.createPayload(forceRegenerate);

        await this.$store.dispatch(SEND_REPAIR_SELECT_REQUEST, payload);
        await this.$store.dispatch(REPAIR_SELECT_REQUEST, this.chatboxMetadata);

        this.summaryAlreadyRequested = true;
        this.toggleLoading(true);

        this.loadingProgress = 0.05;
        this.loadingMessage = 'Returning answer..';
      } catch (error) {
        this.toggleLoading(false);
        console.error('Error sending message:', error);
        this.addSystemMessage('An error occurred while processing the data');
      }
    },
    closeChatBox() {
      this.$emit('close');
      this.forcePopup = false;
    },
    clearHistory() {
      this.$store.dispatch('ChatModule/resetSession', this.sessionKey);
      this.resetChatBox();
    },
    initializeDialogPosition() {
      const saved = sessionStorage.getItem('dialogState');
      if (saved) {
        const state = JSON.parse(saved);
        this.position = state.position || {
          x: window.innerWidth - 520,
          y: window.innerHeight - 720,
        };
        this.width = state.width || 480;
        this.height = state.height || 600;
      } else {
        this.position = {
          x: window.innerWidth - 520,
          y: window.innerHeight - 720,
        };
        this.width = 480;
        this.height = 600;
      }
    },
    startDrag(event) {
      if (
        event.target.closest('button') ||
        event.target.closest('.action-icon')
      ) {
        return;
      }
      if (
        event.target.closest('.drag-icon') ||
        event.target.closest('.dialog-header')
      ) {
        this.isDragging = true;
      }
    },
    stopDrag() {
      this.isDragging = false;
    },
    onDragStop(left, top) {
      this.position = { x: left, y: top };

      sessionStorage.setItem(
        'dialogState',
        JSON.stringify({
          position: this.position,
          width: this.width,
          height: this.height,
        })
      );
    },
    onResizeStop(left, top, width, height) {
      this.position = { x: left, y: top };
      this.width = width;
      this.height = height;

      sessionStorage.setItem(
        'dialogState',
        JSON.stringify({
          position: this.position,
          width: this.width,
          height: this.height,
        })
      );
    },
    addMessage(newMessage) {
      if (!this.sessionKey) return;

      const userMessage = { text: newMessage, isUser: true };
      const interaction = this.createInteraction(newMessage);

      this.$store.dispatch('ChatModule/appendMessage', {
        sessionKey: this.sessionKey,
        message: userMessage,
      });
      this.$store.dispatch('ChatModule/appendInteraction', {
        sessionKey: this.sessionKey,
        interaction,
      });

      this.parseMessage(newMessage);
    },

    addSystemMessage(text) {
      if (!this.sessionKey || !text) return;

      const msg = { text, isUser: false };

      this.$store.dispatch('ChatModule/appendMessage', {
        sessionKey: this.sessionKey,
        message: msg,
      });
    },

    parseMessage(message) {
      const systemMsg = this.strategy.parseUserMessage(message);
      this.addSystemMessage(systemMsg);
      setTimeout(() => this.sendRequest(), 1000);
    },
  },
};
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s ease;
}

.fade-enter-from {
  opacity: 0;
}
.fade-enter-to {
  opacity: 1;
}
.fade-leave-from {
  opacity: 1;
}
.fade-leave-to {
  opacity: 0;
}

.dialog-container {
  width: 100%;
  height: 100%;
}

.dialog {
  width: 100%;
  height: 100%;
  background-color: #f7f7f7;
  border-radius: 10px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  border: 1px solid #ccc;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  z-index: 1000;
  pointer-events: auto;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.18), 0 0 10px rgba(91, 62, 165, 0.3);
  border-radius: 12px;
}

.dialog ::-webkit-scrollbar {
  width: 6px;
}

.dialog ::-webkit-scrollbar-thumb {
  background-color: #5b3ea5;
  border-radius: 3px;
}

.fade-enter-active {
  transition: opacity 0.6s ease;
}

.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  transform: translate3d(0, 0, 0) scale(0.98);
}
.fade-enter-to,
.fade-leave-from {
  transform: translate3d(0, 0, 0) scale(1);
}
</style>
